import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import withRouter from '../../../components/withRouter';
import { useProfile } from '../../../hooks/UserHooks';
import { getGamStatus, connectGam, disconnectGam } from '../services/integrationApi';
import '../styles/integration.css';

import googleAdManagerLogo from '../../../assets/integrations/google-ad-manager.png';
import googleAnalyticsLogo from '../../../assets/integrations/google-analytics.png';
import metaAdsLogo from '../../../assets/integrations/meta.png';
import kwaiLogo from '../../../assets/integrations/kwai.png';
import tiktokLogo from '../../../assets/integrations/tiktok.png';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface GamStatus {
    connected: boolean;
    gamEmail?: string;
    connectedAt?: string;
    networkCode?: string;
}

const comingSoonIntegrations = [
    { name: 'Google Analytics', logo: googleAnalyticsLogo },
    { name: 'Meta Ads', logo: metaAdsLogo },
    { name: 'Kwai Business', logo: kwaiLogo },
    { name: 'TikTok Business', logo: tiktokLogo },
];

const Index = () => {
    const { userProfile } = useProfile();
    const [searchParams, setSearchParams] = useSearchParams();
    const [gamStatus, setGamStatus] = useState<GamStatus>({ connected: false });
    const [gamLoading, setGamLoading] = useState(true);
    const [gamActionLoading, setGamActionLoading] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    const fetchGamStatus = useCallback(async () => {
        try {
            setGamLoading(true);
            const status = await getGamStatus();
            setGamStatus(status);
        } catch {
            setGamStatus({ connected: false });
        } finally {
            setGamLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'Integrações | Ad Pulse';
        fetchGamStatus();
    }, [fetchGamStatus]);

    useEffect(() => {
        const gamResult = searchParams.get('gam');
        if (gamResult === 'success') {
            setToast({ message: 'Google Ad Manager conectado com sucesso!', type: 'success' });
            fetchGamStatus();
            const newSearchParams = new URLSearchParams(searchParams.toString());
            newSearchParams.delete('gam');
            setSearchParams(newSearchParams, { replace: true });
        } else if (gamResult === 'error') {
            setToast({ message: 'Erro ao conectar o Google Ad Manager. Tente novamente.', type: 'error' });
            const newSearchParams = new URLSearchParams(searchParams.toString());
            newSearchParams.delete('gam');
            setSearchParams(newSearchParams, { replace: true });
        }
    }, [searchParams, setSearchParams, fetchGamStatus]);

    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    const handleConnect = async () => {
        try {
            setGamActionLoading(true);
            const result = await connectGam();
            const url = result && typeof result === 'object' ? (result as { url?: string }).url : undefined;

            if (url) {
                window.location.href = url;
            } else {
                setToast({
                    message: 'Erro ao iniciar conexão. Resposta inesperada do servidor.',
                    type: 'error',
                });
            }
        } catch {
            setToast({ message: 'Erro ao iniciar conexão. Verifique se você está vinculado a um publisher.', type: 'error' });
        } finally {
            setGamActionLoading(false);
        }
    };

    const handleDisconnect = async () => {
        try {
            setGamActionLoading(true);
            await disconnectGam();
            setGamStatus({ connected: false });
            setToast({ message: 'Google Ad Manager desconectado.', type: 'success' });
        } catch {
            setToast({ message: 'Erro ao desconectar.', type: 'error' });
        } finally {
            setGamActionLoading(false);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content integrations-page">
                <div className="container">
                    <h2 className="text-2xl font-bold mb-4">Integrações</h2>

                    {toast && (
                        <Alert variant={toast.type === 'success' ? 'default' : 'destructive'} className="mb-4">
                            <AlertDescription>{toast.message}</AlertDescription>
                        </Alert>
                    )}

                    <div className="row">
                        {/* Google Ad Manager - Active Integration */}
                        <div className="col-md-4 mb-4">
                            <Card className="integration-card text-center h-full">
                                <CardContent className="flex flex-col items-center justify-center p-6">
                                    <img
                                        src={googleAdManagerLogo}
                                        alt="Google Ad Manager"
                                        className="integration-logo mb-4"
                                        style={{ maxWidth: '80px', maxHeight: '80px' }}
                                    />
                                    <h5 className="text-lg font-semibold mb-4">Google Ad Manager</h5>

                                    {gamLoading ? (
                                        <Loader2 className="h-5 w-5 animate-spin mt-2" />
                                    ) : gamStatus.connected ? (
                                        <div className="mt-2 w-full space-y-2">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mb-2">
                                                Conectado
                                            </span>
                                            {gamStatus.gamEmail && (
                                                <p className="text-sm text-muted-foreground mb-1">
                                                    <i className="ri-mail-line mr-1"></i>
                                                    {gamStatus.gamEmail}
                                                </p>
                                            )}
                                            {gamStatus.networkCode && (
                                                <p className="text-sm text-muted-foreground mb-2">
                                                    <i className="ri-server-line mr-1"></i>
                                                    Network: {gamStatus.networkCode}
                                                </p>
                                            )}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={handleDisconnect}
                                                disabled={gamActionLoading}
                                                className="w-full"
                                            >
                                                {gamActionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                                Desconectar
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            size="sm"
                                            className="mt-2 w-full"
                                            onClick={handleConnect}
                                            disabled={gamActionLoading}
                                        >
                                            {gamActionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                            Conectar
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {comingSoonIntegrations.map((integration) => (
                            <div className="col-md-4 mb-4" key={integration.name}>
                                <Card className="integration-card text-center integration-soon h-full">
                                    <CardContent className="flex flex-col items-center justify-center p-6">
                                        <img
                                            src={integration.logo}
                                            alt={integration.name}
                                            className="integration-logo mb-4"
                                            style={{ maxWidth: '80px', maxHeight: '80px' }}
                                        />
                                        <h5 className="text-lg font-semibold mb-4">{integration.name}</h5>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
                                            Em breve
                                        </span>
                                    </CardContent>
                                </Card>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);
